import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationEvent, NotificationSettings, PushTestResponse, SiteEvent } from '@ste/shared';
import { apiFetch, rootFetch } from './auth.ts';
import { useConnection } from './connection.ts';

/** event log: from the gateway locally, from the cloud copy in STE Connect */
export const useEvents = (enabled = true) =>
  useQuery({
    queryKey: ['events'],
    queryFn: () => apiFetch<{ events: SiteEvent[] }>('/events?limit=50'),
    refetchInterval: 60_000,
    enabled,
  });

export function useNotificationSettings() {
  const mode = useConnection((s) => s.mode);
  const siteId = useConnection((s) => s.siteId);
  return useQuery({
    queryKey: ['notifications', siteId],
    queryFn: () => apiFetch<NotificationSettings>('/notifications'),
    enabled: mode === 'cloud' && !!siteId,
  });
}

export function usePushStatus() {
  const mode = useConnection((s) => s.mode);
  return useQuery({
    queryKey: ['cloud', 'push'],
    queryFn: () => rootFetch<{ enabled: boolean; devices: number }>('/me/push'),
    enabled: mode === 'cloud',
  });
}

export function useNotificationCommands() {
  const client = useQueryClient();
  const siteId = useConnection((s) => s.siteId);
  return {
    save: useMutation({
      mutationFn: (events: NotificationEvent[]) =>
        apiFetch<NotificationSettings>('/notifications', {
          method: 'PUT',
          body: JSON.stringify({ events }),
        }),
      onMutate: (events) => {
        // optimistic: the switch moves at once
        client.setQueryData<NotificationSettings>(['notifications', siteId], (old) =>
          old ? { ...old, events } : old,
        );
      },
      onSettled: () => void client.invalidateQueries({ queryKey: ['notifications', siteId] }),
    }),
    test: useMutation({
      mutationFn: () => rootFetch<PushTestResponse>('/me/push/test', { method: 'POST' }),
      onSettled: () => void client.invalidateQueries({ queryKey: ['cloud', 'push'] }),
    }),
  };
}
